/**
 * Personalized Plan Hook
 * 
 * Manages the personalized plan state by applying deltas when answers change.
 * Integrates with change notifications to show users what changed.
 * 
 * @see docs/project/specs/dynamic-template-system-v2-spec.md
 */

import { useEffect, useMemo, useRef } from 'react'
import { applyDeltas } from '../utils/apply-deltas'
import { useChangeNotifications } from './useChangeNotifications'
import type { AnswerDelta, PersonalizedTemplate } from '../utils/apply-deltas'
import type { PlanSnapshot } from '../utils/plan-diff'
import type { ProjectTemplate, TemplateStep } from '../types'

interface UsePersonalizedPlanOptions {
  template: ProjectTemplate
  answers: Record<string, string>
  deltas?: Array<AnswerDelta>
  enabled?: boolean
}

interface UsePersonalizedPlanResult {
  personalizedSteps: Array<TemplateStep>
  estimatedHours: number
  estimatedCostMin: number
  estimatedCostMax: number
  modifiedStepSlugs: Set<string>
  isPersonalized: boolean
}

/**
 * Hook to manage personalized plan state.
 * 
 * Applies deltas when answers change and tracks which steps were modified.
 */
export function usePersonalizedPlan({
  template,
  answers,
  deltas = [],
  enabled = true,
}: UsePersonalizedPlanOptions): UsePersonalizedPlanResult {
  const previousAnswersRef = useRef<Record<string, string>>({})
  const baseSnapshotRef = useRef<PlanSnapshot | null>(null)
  
  // Use change notifications
  const { recordSnapshot, compareAndNotify } = useChangeNotifications({
    templateSlug: template.slug,
    enabled,
  })
  
  // Compute personalized plan
  const personalized = useMemo((): PersonalizedTemplate => {
    if (!enabled || deltas.length === 0 || Object.keys(answers).length === 0) {
      return {
        title: template.title,
        estimatedHours: template.estimatedHours ?? 0,
        estimatedCostMin: template.estimatedCostMin ?? 0,
        estimatedCostMax: template.estimatedCostMax ?? 0,
        difficulty: template.difficulty,
        steps: template.steps,
        modifiedStepIndices: new Set(),
      }
    }
    
    return applyDeltas(template, answers, deltas)
  }, [template, answers, deltas, enabled])
  
  // Record base snapshot on mount
  useEffect(() => {
    if (!baseSnapshotRef.current) {
      baseSnapshotRef.current = {
        estimatedHours: template.estimatedHours,
        estimatedCostMin: template.estimatedCostMin,
        estimatedCostMax: template.estimatedCostMax,
        steps: template.steps,
      }
      recordSnapshot(baseSnapshotRef.current)
    }
  }, [template, recordSnapshot])
  
  // Compare and notify when personalized plan changes
  useEffect(() => {
    const prevAnswers = previousAnswersRef.current
    const hasAnswerChanges = Object.keys(answers).some(
      key => answers[key] !== prevAnswers[key]
    ) || Object.keys(prevAnswers).some(
      key => answers[key] !== prevAnswers[key]
    )
    
    if (hasAnswerChanges && Object.keys(prevAnswers).length > 0) {
      const newSnapshot: PlanSnapshot = {
        estimatedHours: personalized.estimatedHours,
        estimatedCostMin: personalized.estimatedCostMin,
        estimatedCostMax: personalized.estimatedCostMax,
        steps: personalized.steps,
      }
      
      compareAndNotify(newSnapshot)
    }
    
    previousAnswersRef.current = { ...answers }
  }, [answers, personalized, compareAndNotify])
  
  /** Slugs for steps currently touched by applied deltas only (avoids stale ✨ from accumulated diff history). */
  const modifiedStepSlugs = useMemo(() => {
    const slugs = new Set<string>()
    for (const idx of personalized.modifiedStepIndices) {
      const step = personalized.steps[idx] as (typeof personalized.steps)[number] | undefined
      if (step?.slug) {
        slugs.add(step.slug)
      }
    }
    return slugs
  }, [personalized.modifiedStepIndices, personalized.steps])

  const isPersonalized =
    personalized.modifiedStepIndices.size > 0 ||
    personalized.estimatedHours !== (template.estimatedHours ?? 0) ||
    personalized.estimatedCostMin !== (template.estimatedCostMin ?? 0) ||
    personalized.estimatedCostMax !== (template.estimatedCostMax ?? 0)
  
  return {
    personalizedSteps: personalized.steps,
    estimatedHours: personalized.estimatedHours,
    estimatedCostMin: personalized.estimatedCostMin,
    estimatedCostMax: personalized.estimatedCostMax,
    modifiedStepSlugs,
    isPersonalized,
  }
}
